import { getSession } from '../auth';
import { toast } from '../components/toast';
import { createCourse, deleteCourse, isProductionData, listCourses } from '../lib/repository';
import { openModal, closeModal } from '../components/modal';
import type { CourseRecord } from '../domain';

type CourseTab = 'all' | 'live' | 'draft' | 'archived';

let courseCache: CourseRecord[] = [];
let loaded = false;
let loading = false;
let activeTab: CourseTab = 'all';

async function loadCourses() {
  if (!isProductionData() || loaded || loading) return;
  loading = true;
  courseCache = [];   // reset so a permission-denied retry doesn't show stale data
  try {
    courseCache = await listCourses();
    loaded = true;
  } catch (err) {
    // Non-admin users without membership doc see empty list instead of crashing.
    courseCache = [];
    loaded = true;
    if ((window as any).__toast) (window as any).__toast('warn', err instanceof Error ? err.message : 'Could not load courses');
  }
  loading = false;
  (window as any).__rerender?.();
}

function courseStatus(c: CourseRecord): string { return c.status || 'draft'; }

export function renderCourses(): string {
  void loadCourses();
  const session = getSession();
  const canManageCourses = !!session && (session.role === 'owner' || session.role === 'admin');
  const filtered = activeTab === 'all' ? courseCache : courseCache.filter((c) => courseStatus(c) === activeTab);

  const tabs: Array<[CourseTab, string]> = [['all', 'All'], ['live', 'Live'], ['draft', 'Drafting'], ['archived', 'Archived']];
  const tabBtns = tabs.map(([k, label]) => '<button class="touch-target ' + (activeTab === k ? 'active' : '') + '" data-course-tab="' + k + '">' + label + '</button>').join('');
  const cards = filtered.map((c) =>
    '<button class="course-card card" data-course-select="' + c.id + '" style="text-align:left;cursor:pointer;width:100%;border:1px solid var(--rule);border-radius:14px;padding:18px;background:var(--bg-elev);">' +
      '<div class="row"><span class="pill ' + (courseStatus(c) === 'live' ? 'pill-success' : 'pill-info') + '">' + courseStatus(c) + '</span><span class="spacer"></span><span class="t-mono t-meta">' + (c.code || '—') + '</span></div>' +
      '<h3 class="t-h3" style="margin:10px 0 6px">' + (c.title || 'Untitled') + '</h3>' +
      '<div class="t-meta">' + (c.durationWeeks || 6) + ' weeks · ' + (c.level || 'Foundation') + '</div>' +
    '</button>').join('');
  const emptyCopy = activeTab === 'all' ? 'No courses yet — create your first one.' : 'No ' + activeTab + ' courses right now.';
  const empty = filtered.length ? '' : ('<div class="card card-pad-lg empty"><div class="empty-illus">＋</div><h2>' + (activeTab === 'all' ? 'Create your first course' : 'Nothing in this tab') + '</h2><p style="font-size:var(--t-sm)">' + emptyCopy + '</p></div>');

  return '<section class="hero stagger"><div><div class="hero-greet">Course catalog · ' + (loading ? 'loading…' : courseCache.length + ' ' + (courseCache.length === 1 ? 'program' : 'programs')) + '</div><h1 class="hero-title">What\u2019s running<br/><span class="hero-stroke">this season.</span></h1></div>' +
    '<div class="hero-aside">' + (canManageCourses ? '<button class="btn btn-accent touch-target" data-action="course-new">+ New course</button>' : '<span class="pill pill-info">View only</span>') + '</div></section>' +
    '<div class="tabs" style="margin-bottom:var(--s-4)">' + tabBtns + '</div>' +
    '<div class="course-grid stagger">' + cards + '</div>' + empty;
}

export function bindCourses(): void {
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const tab = t.closest<HTMLElement>('[data-course-tab]');
    if (tab && tab.dataset.courseTab) { activeTab = tab.dataset.courseTab as CourseTab; (window as any).__rerender?.(); return; }
    const newBtn = t.closest<HTMLElement>('[data-action="course-new"]');
    if (newBtn) { openCourseForm(); return; }
    const closeBtn = t.closest<HTMLElement>('[data-course-close]');
    if (closeBtn) { closeModal('course-create'); return; }
    const card = t.closest<HTMLElement>('[data-course-select]');
    if (card && card.dataset.courseSelect) {
      location.hash = '#/course-detail/' + encodeURIComponent(card.dataset.courseSelect);
      return;
    }
    // Delete confirmation inside detail modal
    const del = t.closest<HTMLElement>('[data-course-delete]');
    if (del) {
      const id = del.dataset.courseDelete;
      if (!id) return;
      const c = courseCache.find((x) => x.id === id);
      if (c && window.confirm('Delete course "' + c.title + '"? This cannot be undone.')) {
        void deleteCourse(id).then(() => {
          toast('Course deleted', 'success');
          closeModal('course-detail');
          loaded = false; courseCache = [];
          (window as any).__rerender?.();
        }).catch((err) => toast(err instanceof Error ? err.message : 'Could not delete course', 'warn'));
      }
    }
  });

  document.addEventListener('submit', async (e) => {
    const form = (e.target as HTMLElement).closest<HTMLFormElement>('#course-create-form');
    if (!form) return;
    e.preventDefault();
    const fd = new FormData(form);
    const payload = {
      title: String(fd.get('title') || '').trim(),
      code: String(fd.get('code') || 'NEW-1').toUpperCase(),
      description: String(fd.get('description') || ''),
      durationWeeks: Number.parseInt(String(fd.get('duration') || '6'), 10) || 6,
      level: String(fd.get('level') || 'Foundation'),
      status: String(fd.get('status') || 'draft') as 'draft' | 'live' | 'archived',
    };
    if (!payload.title) { toast('Title is required', 'warn'); return; }
    const btn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (btn) btn.disabled = true;
    try {
      if (isProductionData()) await createCourse(payload);
      loaded = false; courseCache = [];
      toast('Course saved', 'success');
      closeModal('course-create');
      (window as any).__rerender?.();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Course could not be saved', 'warn');
      if (btn) btn.disabled = false;
    }
  });
}

function openCourseDetail_REMOVED(c: CourseRecord): void {
  openModal({
    id: 'course-detail',
    title: c.title || 'Untitled course',
    subtitle: 'Course details',
    width: 560,
    html: '<div class="stack-tight">' +
      '<div class="row"><span class="pill ' + (courseStatus(c) === 'live' ? 'pill-success' : 'pill-info') + '">' + courseStatus(c) + '</span><span class="spacer"></span><span class="t-mono t-meta">' + (c.code || '—') + '</span></div>' +
      '<div class="row"><span class="t-meta" style="flex:1">Duration</span><strong>' + (c.durationWeeks || 6) + ' weeks</strong></div>' +
      '<div class="row"><span class="t-meta" style="flex:1">Level</span><strong>' + (c.level || 'Foundation') + '</strong></div>' +
      (c.description ? '<div class="t-meta" style="line-height:1.6">' + c.description + '</div>' : '') +
      '<div class="divider-rule"></div>' +
      '<div class="row" style="gap:10px;justify-content:flex-end">' +
        '<button class="btn btn-ghost" type="button" data-course-delete="' + c.id + '" style="color:var(--warn,#d97706)">Delete</button>' +
        '<button class="btn btn-primary" type="button" data-course-close>Close</button>' +
      '</div>' +
    '</div>',
  });
}

function openCourseForm(): void {
  openModal({
    id: 'course-create',
    title: 'Create a course',
    subtitle: 'Programme details live in your tenant catalogue.',
    width: 560,
    html: '<form id="course-create-form">' +
      '<div class="stack-tight">' +
        '<label class="t-eyebrow">Course title<input required name="title" placeholder="e.g. Advanced Web Development" class="form-input"/></label>' +
        '<label class="t-eyebrow">Short description<textarea name="description" rows="2" placeholder="One-line summary shown on the catalogue card" class="form-input" style="min-height:auto;resize:vertical"></textarea></label>' +
        '<div class="row" style="gap:12px">' +
          '<label style="flex:1" class="t-eyebrow">Duration<select name="duration" class="form-input"><option>4 wks</option><option selected>6 wks</option><option>8 wks</option><option>10 wks</option><option>12 wks</option></select></label>' +
          '<label style="flex:1" class="t-eyebrow">Level<select name="level" class="form-input"><option>Foundation</option><option>Intermediate</option><option>Advanced</option></select></label>' +
        '</div>' +
        '<div class="row" style="gap:12px">' +
          '<label style="flex:1" class="t-eyebrow">Course code<input name="code" value="NEW-1" required class="form-input"/></label>' +
          '<label style="flex:1" class="t-eyebrow">Initial status<select name="status" class="form-input"><option value="draft" selected>Draft</option><option value="live">Live</option><option value="archived">Archived</option></select></label>' +
        '</div>' +
      '</div>' +
      '<div class="row" style="margin-top:24px;gap:10px;justify-content:flex-end">' +
        '<button class="btn btn-ghost" type="button" data-course-close>Cancel</button>' +
        '<button class="btn btn-primary" type="submit">Save course</button>' +
      '</div>' +
    '</form>',
  });
}
